import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Header = () => {
  const { language, changeLanguage, t } = useLanguage();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const langRef = useRef(null);
  const mobileRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
  ];

  const currentLang = languages.find(l => l.code === language) || languages[0];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setShowLangMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [language]);

  const navLinks = [
    { href: '/trading', label: t.nav.trading },
    { href: '/insurance', label: t.nav.insurance },
    { href: '/tourism', label: t.nav.tourism },
    { href: '/about', label: t.nav.aboutUs },
    { href: '/contact', label: t.nav.contact }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <nav className="bg-neutral-100 border-b border-neutral-300 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 md:gap-3" data-testid="header-logo">
              <img 
                src="/sre-global-logo.png" 
                alt="SRE GLOBAL" 
                className="h-10 md:h-12 w-auto object-contain"
              />
            </a>

            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navLinks.map((link) => (
                <a key={link.href} href={link.href} className="text-neutral-700 hover:text-teal-600 transition-colors whitespace-nowrap">
                  {link.label}
                </a>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Language Selector */}
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setShowLangMenu(!showLangMenu)}
                  className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded-md hover:bg-neutral-200 transition-colors text-neutral-700"
                  data-testid="language-selector"
                >
                  <span className="text-xl md:text-2xl">{currentLang.flag}</span>
                  <ChevronDown size={16} />
                </button>
                {showLangMenu && (
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-md shadow-lg border border-neutral-200 py-2 min-w-[160px] z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setShowLangMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-neutral-100 transition-colors flex items-center gap-3 ${
                          language === lang.code ? 'bg-teal-50 text-teal-700' : 'text-neutral-700'
                        }`}
                        data-testid={`lang-option-${lang.code}`}
                      >
                        <span className="text-xl">{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button className="hidden md:block p-2 hover:bg-neutral-200 rounded-md transition-colors text-neutral-700">
                <Search size={20} />
              </button>

              {/* Mobile Hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 hover:bg-neutral-200 rounded-md transition-colors text-neutral-700"
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-neutral-300 bg-neutral-100" data-testid="mobile-menu-dropdown" ref={mobileRef}>
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block px-4 py-3 text-neutral-700 hover:bg-teal-50 hover:text-teal-700 rounded-md transition-colors font-medium"
                  data-testid={`mobile-nav-${link.href.slice(1)}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
